import { Avatar, Box, Button, Group, SimpleGrid, Skeleton, Stack, Text, Title } from "@mantine/core"
import { ArrowRightIcon } from "@phosphor-icons/react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { HelpIcon, useHelpContext } from "@dehub/theme"
import { useNavigate, useParams } from "react-router"
import useSWR from "swr"
import { DropShippingApi } from "../../apis"
import { PageLayout } from "../layout"
import { usePermission } from "../permission"
import { ListState } from "../ui"
import RenderText from "../ui/render-text"
import classes from "./detail.module.css"
import { SupplierDropShippingForm } from "./form"

type Business = { logo?: string | null; profileName?: string | null; refCode?: string | null; partner?: { businessName?: string | null; refCode?: string | null } | null }

const PartyNode = ({ label, business }: { label: string; business?: Business | null }) => (
  <Stack gap={4} align="center" flex={1} miw={0}>
    <Text fz={14} c="gray.8">{label}</Text>
    <Avatar src={business?.logo || undefined} radius="xl" size="md">
      {(business?.profileName || "-").charAt(0)}
    </Avatar>
    <Text fw={700} fz={14} ta="center" lineClamp={1}>{business?.profileName || "-"}</Text>
    <Text fz={14} c="gray.8" ta="center" lineClamp={1}>{business?.partner?.businessName || "-"}</Text>
    <Text fz={14} c="gray.8" ff="monospace">{business?.refCode || "-"}</Text>
  </Stack>
)

/** Dropship detail with denied, load, error and not-found states (Ord_States), parties and price breakdown on top. */
export function SupplierDropShippingDetail() {
  const params = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(["supplierSales", "common"])
  const ORD_DRSHPING_DETAIL = usePermission("ORD_DRSHPING_DETAIL") // BE order/dropshipping/get.ts
  const ERP_PGH_MARGIN_VIEW = usePermission("ERP_PGH_MARGIN_VIEW")

  const { data, error, isLoading, mutate } = useSWR(
    ORD_DRSHPING_DETAIL.hasAccess ? `/ord/order/dropshipping/detail/get.${params.id}` : null,
    async () => await DropShippingApi.get(params.id as string),
  )

  // @dehub/help: status + side for the drawer, `needsDn` for the «Одоо» card (delivery tab = source supplier delivers).
  const { set: setHelpContext } = useHelpContext()
  const deliveryTab = params.type === "delivery"
  useEffect(() => {
    setHelpContext({ status: data?.orderStatus, side: deliveryTab ? "RECEIVER" : "SENDER", vars: { needsDn: deliveryTab && data && !data.deliveryNote ? 1 : 0, deliveryBy: data?.deliveryBy } })
  }, [data, deliveryTab, setHelpContext])

  const frame = (children: React.ReactNode) => (
    <PageLayout
      title={t("supplierSales:dropshipping.formTitle")}
      breadcrumb={[
        { to: "/", label: t("common:breadcrumb.home") },
        { to: "/supplier-drop-shipping", label: t("supplierSales:dropshipping.breadcrumb") },
        { label: t("supplierSales:dropshipping.breadcrumbDetail") },
      ]}>
      <Box mt="md">{children}</Box>
    </PageLayout>
  )

  if (!ORD_DRSHPING_DETAIL.hasAccess) return frame(ORD_DRSHPING_DETAIL.accessDenied())
  if (isLoading && !data) {
    return frame(
      <Stack gap="md">
        <Skeleton height={160} radius="lg" />
        <Skeleton height={120} radius="lg" />
        <Skeleton height={320} radius="lg" />
      </Stack>,
    )
  }
  const status = (error as { statusCode?: number } | undefined)?.statusCode
  if (status === 404 || (!error && !data)) {
    return frame(
      <ListState
        state="empty"
        title={t("supplierSales:dropshipping.notFoundTitle")}
        description={t("supplierSales:dropshipping.notFoundText")}
        action={{ label: t("supplierSales:dropshipping.backButton"), onClick: () => navigate("/supplier-drop-shipping") }}
      />,
    )
  }
  if (error) {
    return frame(
      <Stack gap="md" align="flex-start">
        <ListState state="error" onRetry={() => mutate()} errorCode={status} />
        <Button variant="default" onClick={() => navigate("/supplier-drop-shipping")}>{t("supplierSales:dropshipping.backButton")}</Button>
      </Stack>,
    )
  }

  const marginPercent = data?.priceBreakdown?.marginPercent
  const showBreakdown = ERP_PGH_MARGIN_VIEW.hasAccess && data?.dropperPriceTotal != null
  const margin = data?.margin ?? (Number(data?.dropperPriceTotal) - Number(data?.totalAmount ?? 0))

  return (
    <>
      <SupplierDropShippingForm
        action={["detail", data]}
        before={
          <>
            <Box className={classes.card} mb="md">
              <Title order={3} className={classes.cardTitle}>{t("supplierSales:dropshipping.transactionParties")}<HelpIcon helpKey="order.supplier-drop-shipping-detail.section.parties" variant="section" /></Title>
              <Group align="flex-start" justify="space-between" wrap="nowrap" gap="xs">
                <PartyNode label={t("supplierSales:dropshipping.parties.sourceSupplier")} business={data.dropshipperBusiness} />
                <ArrowRightIcon size={20} style={{ marginTop: 24, flexShrink: 0 }} />
                <PartyNode label={t("supplierSales:dropshipping.parties.dropper")} business={data.dropperBusiness} />
                <ArrowRightIcon size={20} style={{ marginTop: 24, flexShrink: 0 }} />
                <PartyNode label={t("supplierSales:dropshipping.parties.finalBuyer")} business={data.receiverBusiness} />
              </Group>
            </Box>
            <Box className={classes.card} mb="md">
              <Title order={3} className={classes.cardTitle}>{t("supplierSales:dropshipping.priceBreakdown")}<HelpIcon helpKey="order.supplier-drop-shipping-detail.section.price" variant="section" /></Title>
              {showBreakdown ? (
                <SimpleGrid cols={{ base: 1, sm: 3 }}>
                  <Box className={classes.tile}>
                    <Text className={classes.tileLabel}>{t("supplierSales:dropshipping.columns.dropperPriceTotal")}</Text>
                    <RenderText text={data.dropperPriceTotal} isAmount fontWeight={700} size="lg" />
                  </Box>
                  <Box className={classes.tile}>
                    <Text className={classes.tileLabel}>{t("supplierSales:dropshipping.cost")}</Text>
                    <RenderText text={data.totalAmount ?? 0} isAmount fontWeight={700} size="lg" />
                  </Box>
                  <Box className={classes.tile}>
                    <Text className={classes.tileLabel}>{t("supplierSales:dropshipping.columns.margin")}</Text>
                    <Group gap="xs">
                      <RenderText text={margin} isAmount fontWeight={700} size="lg" />
                      {marginPercent != null && <Text fz={14} c="gray.8">({marginPercent}%)</Text>}
                    </Group>
                  </Box>
                </SimpleGrid>
              ) : (
                <Text fz={14} c="gray.8">{ERP_PGH_MARGIN_VIEW.reason}</Text>
              )}
            </Box>
          </>
        }
      />
    </>
  )
}
